import React, { useEffect, useId, useRef, useState } from "react";
import { useMutationLock } from "../hooks/useMutationLock";
import "./UserListStatusModal.css";

const REASON_MAX = 500;

const TITLES = {
  grey: "Grey list student",
  black: "Black list student",
  view: "List reason",
};

export default function UserListStatusModal({
  mode,
  user,
  onClose,
  onSubmit,
}) {
  const titleId = useId();
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const { busy, run } = useMutationLock();
  const [reason, setReason] = useState(
    mode === "view" ? user?.listReason || "" : "",
  );
  const [error, setError] = useState("");

  const isView = mode === "view";
  const trimmed = reason.trim();
  const canSubmit =
    !isView && trimmed.length > 0 && trimmed.length <= REASON_MAX && !busy;

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeBtnRef.current?.focus();

    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (busy) return;
        e.preventDefault();
        onClose?.();
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose, busy]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError("");

    await run(async () => {
      try {
        await onSubmit({
          blackListed: mode === "black",
          greyListed: mode === "grey",
          reason: trimmed,
        });
        onClose?.();
      } catch (err) {
        setError(
          err?.response?.data?.message || "Failed to update list status.",
        );
      }
    });
  };

  const studentLabel = user?.name || user?.email || "Student";

  return (
    <div
      className="user-list-status-overlay"
      role="presentation"
      onMouseDown={(e) => {
        if (busy) return;
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        ref={dialogRef}
        className="user-list-status-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
      >
        <header className="user-list-status-dialog__header">
          <div>
            <p className="user-list-status-dialog__eyebrow">{studentLabel}</p>
            <h2 id={titleId} className="user-list-status-dialog__title">
              {TITLES[mode] || TITLES.view}
            </h2>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            className="user-list-status-dialog__close"
            onClick={onClose}
            aria-label="Close"
            disabled={busy}
          >
            ×
          </button>
        </header>

        {isView ? (
          <div className="user-list-status-dialog__body">
            <p className="user-list-status-dialog__reason-text">
              {user?.listReason?.trim() || "No reason recorded."}
            </p>
            <div className="user-list-status-dialog__footer">
              <button
                type="button"
                className="user-list-status-dialog__cancel"
                onClick={onClose}
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="user-list-status-dialog__body">
              <p className="user-list-status-dialog__intro">
                Provide a reason for this listing. The reason is required and
                will be visible to admins.
              </p>
              <label
                className="user-list-status-dialog__label"
                htmlFor="list-status-reason"
              >
                Reason
              </label>
              <textarea
                id="list-status-reason"
                className="user-list-status-dialog__textarea"
                rows={5}
                maxLength={REASON_MAX}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is this student being listed?"
                required
                disabled={busy}
                autoFocus
              />
              <div className="user-list-status-dialog__counter">
                {reason.length}/{REASON_MAX}
              </div>
              {error ? (
                <p className="user-list-status-dialog__error">{error}</p>
              ) : null}
            </div>
            <div className="user-list-status-dialog__footer">
              <button
                type="button"
                className="user-list-status-dialog__cancel"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`user-list-status-dialog__submit user-list-status-dialog__submit--${mode}`}
                disabled={!canSubmit}
              >
                {busy
                  ? "Saving…"
                  : mode === "black"
                    ? "Black list"
                    : "Grey list"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
